import React, { useEffect, useRef, useState } from "react";
import { X, Download, ZoomIn, ZoomOut, Maximize2, Pencil, Loader2, Send, Pin, PinOff, ChevronLeft, ChevronRight } from "lucide-react";
import FeedbackWidget from "./FeedbackWidget";
import VoiceMicButton from "./VoiceMicButton";

/**
 * Fullscreen image lightbox with wheel + pinch zoom and pan.
 * Props:
 *   open:boolean, src:string, onClose:fn, onDownload?:fn,
 *   caption?:string,
 *   renderId?:string  (enables thumbs feedback widget),
 *   pinned?:boolean,
 *   onTogglePin?: async () => void,
 *   onEdit?: async (instruction) => string|null   // returns new src on success
 */
export default function Lightbox({
  open,
  src,
  onClose,
  onDownload,
  caption,
  renderId,
  pinned,
  onTogglePin,
  onEdit,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const pinchStart = useRef(null);

  // Edit-prompt state
  const [editing, setEditing] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const micRef = useRef(null);

  // Reset on (re)open
  useEffect(() => {
    if (!open) return;
    setScale(1);
    setTx(0);
    setTy(0);
    setEditing(false);
    setInstruction("");
    setEditError("");
    setEditLoading(false);
  }, [open]);

  // Keyboard shortcuts — re-binds when editing flips so Esc behaves correctly
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (editing) setEditing(false);
        else onClose();
      }
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(6, s + 0.4));
      if (e.key === "-") setScale((s) => Math.max(1, s - 0.4));
      if (e.key === "0") {
        setScale(1);
        setTx(0);
        setTy(0);
      }
      if (e.key === "ArrowLeft" && hasPrev && !editing && onPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext && !editing && onNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, editing, onPrev, onNext, hasPrev, hasNext]);

  if (!open) return null;

  const submitEdit = async () => {
    if (!onEdit || editLoading) return;
    setEditError("");
    setEditLoading(true);
    try {
      // If the mic is currently recording, stop + transcribe first and
      // append/merge the voice text into the typed instruction. One tap on
      // Apply does the whole "stop talking → transcribe → apply edit" flow.
      let finalInstruction = instruction.trim();
      const voiceText = micRef.current ? await micRef.current.flushIfRecording() : null;
      if (voiceText) {
        finalInstruction = finalInstruction
          ? `${finalInstruction} ${voiceText}`.slice(0, 500)
          : voiceText.slice(0, 500);
        setInstruction(finalInstruction);
      }
      if (!finalInstruction) {
        setEditError("Please type or speak a small change first.");
        setEditLoading(false);
        return;
      }
      const result = await onEdit(finalInstruction);
      if (result) {
        // Parent updates the src — just collapse the input
        setEditing(false);
        setInstruction("");
      } else {
        setEditError("Edit failed. Please try a different instruction.");
      }
    } catch (e) {
      setEditError(e?.message || "Edit failed. Please try again.");
    } finally {
      setEditLoading(false);
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY / 600;
    setScale((s) => Math.min(6, Math.max(1, s + delta)));
  };
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    setTx((x) => x + (e.clientX - last.current.x));
    setTy((y) => y + (e.clientY - last.current.y));
    last.current = { x: e.clientX, y: e.clientY };
  };
  const stopDrag = () => (dragging.current = false);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStart.current = { dist: Math.hypot(dx, dy), scale };
    } else if (e.touches.length === 1 && scale > 1) {
      dragging.current = true;
      last.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStart.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / pinchStart.current.dist;
      setScale(Math.min(6, Math.max(1, pinchStart.current.scale * ratio)));
    } else if (e.touches.length === 1 && dragging.current) {
      setTx((x) => x + (e.touches[0].clientX - last.current.x));
      setTy((y) => y + (e.touches[0].clientY - last.current.y));
      last.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  const onTouchEnd = () => {
    pinchStart.current = null;
    dragging.current = false;
  };

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95"
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
    >
      {hasPrev && !editing && (
        <button
          type="button"
          data-testid="lightbox-prev"
          onClick={onPrev}
          aria-label="Previous render"
          title="Previous (←)"
          className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2.5 text-black hover:bg-white/90"
        >
          <ChevronLeft size={16} />
        </button>
      )}
      {hasNext && !editing && (
        <button
          type="button"
          data-testid="lightbox-next"
          onClick={onNext}
          aria-label="Next render"
          title="Next (→)"
          className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2.5 text-black hover:bg-white/90"
        >
          <ChevronRight size={16} />
        </button>
      )}
      <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
        <button
          data-testid="lightbox-zoom-out"
          onClick={() => setScale((s) => Math.max(1, s - 0.4))}
          className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
        >
          <ZoomOut size={16} />
        </button>
        <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white">
          {Math.round(scale * 100)}%
        </span>
        <button
          data-testid="lightbox-zoom-in"
          onClick={() => setScale((s) => Math.min(6, s + 0.4))}
          className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
        >
          <ZoomIn size={16} />
        </button>
        <button
          data-testid="lightbox-fit"
          onClick={() => {
            setScale(1);
            setTx(0);
            setTy(0);
          }}
          className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
          title="Fit"
        >
          <Maximize2 size={16} />
        </button>
        {onTogglePin && (
          <button
            data-testid="lightbox-pin"
            onClick={onTogglePin}
            className={[
              "rounded-full p-2.5 text-white transition",
              pinned ? "bg-heda-gold/90 hover:bg-heda-gold" : "bg-white/10 hover:bg-white/20",
            ].join(" ")}
            title={pinned ? "Unpin (allow auto-delete)" : "Save forever"}
          >
            {pinned ? <PinOff size={16} /> : <Pin size={16} />}
          </button>
        )}
        {onEdit && (
          <button
            data-testid="lightbox-edit"
            onClick={() => {
              setEditing((v) => !v);
              setEditError("");
            }}
            className={[
              "rounded-full p-2.5 text-white transition",
              editing ? "bg-heda-gold/90 hover:bg-heda-gold" : "bg-white/10 hover:bg-white/20",
            ].join(" ")}
            title="Edit with AI"
          >
            <Pencil size={16} />
          </button>
        )}
        {onDownload && (
          <button
            data-testid="lightbox-download"
            onClick={onDownload}
            className="rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"
            title="Download"
          >
            <Download size={16} />
          </button>
        )}
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          className="rounded-full bg-white p-2.5 text-black hover:bg-white/90"
          title="Close (Esc)"
        >
          <X size={16} />
        </button>
      </div>

      {caption && !editing && !editLoading && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-xs text-white/90">
          {caption}
        </div>
      )}

      {/* Feedback widget — small thumbs in the bottom-left, only when we have a renderId */}
      {renderId && !editing && !editLoading && (
        <FeedbackWidget
          renderId={renderId}
          position="bl"
          variant="dark"
          className="!bottom-4 !left-4"
        />
      )}

      {/* Edit prompt bar — slides up from bottom when pencil is tapped */}
      {editing && onEdit && (
        <div
          data-testid="lightbox-edit-bar"
          className="absolute inset-x-3 bottom-3 z-20 mx-auto max-w-xl rounded-2xl border border-white/10 bg-black/70 p-2 backdrop-blur-md sm:p-2.5"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2">
            <Pencil size={14} className="ml-1 shrink-0 text-heda-gold" />
            <input
              data-testid="lightbox-edit-input"
              type="text"
              autoFocus
              maxLength={500}
              placeholder="Describe a small change — type or tap the mic to speak in any language"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitEdit();
              }}
              disabled={editLoading}
              className="flex-1 bg-transparent px-1 py-1.5 text-[13px] text-white placeholder:text-white/40 outline-none disabled:opacity-50"
            />
            <VoiceMicButton
              ref={micRef}
              disabled={editLoading}
              onTranscribed={(text) => {
                // If user taps the mic to stop themselves, just append to the
                // instruction so they can review before tapping Apply.
                setInstruction((cur) =>
                  cur.trim() ? `${cur.trim()} ${text}`.slice(0, 500) : text.slice(0, 500)
                );
              }}
            />
            <button
              data-testid="lightbox-edit-submit"
              onClick={submitEdit}
              disabled={editLoading}
              className="flex shrink-0 items-center gap-1 rounded-full bg-heda-gold px-3 py-1.5 text-[12px] font-semibold text-white transition hover:bg-heda-gold/90 disabled:opacity-40"
            >
              {editLoading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Send size={13} />
              )}
              Apply
            </button>
          </div>
          {editError && (
            <div
              data-testid="lightbox-edit-error"
              className="mt-1.5 px-1 text-[11.5px] text-red-300"
            >
              {editError}
            </div>
          )}
        </div>
      )}

      {/* Loading overlay while AI is editing */}
      {editLoading && (
        <div
          data-testid="lightbox-edit-loading"
          className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/45 backdrop-blur-[1px]"
        >
          <Loader2 size={36} className="mb-3 animate-spin text-heda-gold" />
          <div className="px-6 text-center font-display text-base font-semibold text-white">
            Refining your render…
          </div>
          <div className="mt-1 px-6 text-center text-[12px] text-white/70">
            Keeping the scene exact, applying just your change.
          </div>
        </div>
      )}

      <div
        className="flex h-full w-full items-center justify-center overflow-hidden touch-none"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          data-testid="lightbox-img"
          src={src}
          alt={caption || "Render"}
          draggable={false}
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: dragging.current || pinchStart.current ? "none" : "transform .15s ease-out",
            cursor: scale > 1 ? "grab" : "zoom-in",
          }}
          className="block max-h-full max-w-full select-none object-contain"
          onClick={() => scale === 1 && setScale(2)}
        />
      </div>
    </div>
  );
}
